import { faker } from "@faker-js/faker";
import { Factory } from "fishery";
import { PaymentPrefAddressObjectType, WireBankFullFragment } from "#graphql/supergraphTypes";
import { toGlobalId } from "#utils/graphql_utils";
import { RedactedPaymentPref, DetailedPaymentPref } from "..";
import { PaymentMethod } from "../common/enums";
import { WorkflowMode } from "../components/PaymentPrefConsolidated/common/enums";
import {
  DetailedAchPaymentPrefObjectType,
  DetailedBecsPaymentPrefObjectType,
  DetailedEftPaymentPrefObjectType,
  DetailedBacsPaymentPrefObjectType,
  DetailedWirePaymentPrefObjectType,
  DetailedCheckPaymentPrefObjectType,
  DetailedSepaPaymentPrefObjectType,
  DetailedNonUsPaymentPrefObjectType,
  Position,
} from "../components/PaymentPrefConsolidated/common/types";
import { CheckPaymentPrefInputType } from "../components/PaymentPrefConsolidated/Components/CheckPaymentPrefInput/constants";
import { getBankInfoRedacted } from "../components/PaymentPrefConsolidated/Components/CheckPaymentPrefInput/utils";
import {
  AccountPositionObjectType,
  ConsolidatedPaymentPrefContextType,
  PaymentMethodValue,
  PositionEdge,
  PositionConnection,
  AccountObjectType,
  AccountEdge,
} from "../components/PaymentPrefConsolidated/Context/types";
import { subscriptionAddressFactory } from "./subscriptionAddress.factory";

export const consolidatedPaymentPrefPositionFactory: Factory<Position> =
  Factory.define<Position>(({ params, sequence }) => ({
    id: params.id ?? toGlobalId("position", sequence),
    investmentEntityId: params.investmentEntityId ?? toGlobalId("investmentEntity", sequence),
    investmentEntityName: params.investmentEntityName ?? "Some entity name",
    accountId: params.accountId ?? toGlobalId("account", sequence),
  }));

export const paymentMethodValueFactory = Factory.define<PaymentMethodValue>(() => ({
  isAvailable: true,
  warning: "",
}));

export const paymentMethodsFactory = Factory.define<Record<PaymentMethod, PaymentMethodValue>>(
  () => ({
    [PaymentMethod.ACH]: paymentMethodValueFactory.build(),
    [PaymentMethod.BECS]: paymentMethodValueFactory.build(),
    [PaymentMethod.EFT]: paymentMethodValueFactory.build(),
    [PaymentMethod.BACS]: paymentMethodValueFactory.build(),
    [PaymentMethod.SEPA]: paymentMethodValueFactory.build(),
    [PaymentMethod.Wire]: paymentMethodValueFactory.build(),
    [PaymentMethod.Check]: paymentMethodValueFactory.build(),
    [PaymentMethod.Other]: paymentMethodValueFactory.build(),
  }),
);

export const consolidatedPaymentPrefContextFactory =
  Factory.define<ConsolidatedPaymentPrefContextType>(() => ({
    setAssociatePaymentPref: () => undefined,
    setSelectAvailablePaymentPrefsFormHeaderComponent: () => undefined,
    setListOfPositionsToUpdateComponent: () => undefined,
    paymentMethods: paymentMethodsFactory.build(),
    setSelectedPaymentMethod: () => undefined,
    setPaymentPrefDetail: () => undefined,
    setSelectedPaymentPref: () => undefined,
    isExistingPaymentPref: false,
    setIsExistingPaymentPref: () => undefined,
    setPaymentPrefId: () => undefined,
    setUpdatedPositionsPerAccount: () => undefined,
    workflowMode: WorkflowMode.Create,
    setRequestReason: () => undefined,
    approvalCompleted: false,
    setApprovalCompleted: () => undefined,
    setSelectedBankCountry: () => undefined,
    bankCountryLocked: false,
    setBankCountryLocked: () => undefined,
    setAvailablePaymentRailsResponse: () => undefined,
    setOtherPaymentPrefInstructions: () => undefined,
  }));
export const investmentEntityFactory = Factory.define<
  AccountPositionObjectType["investmentEntity"]
>(({ sequence }) => ({
  __typename: "AccountObjectType",
  id: toGlobalId("AccountObjectType", sequence),
  name: faker.company.name(),
}));
export const positionObjectTypeFactory = Factory.define<AccountPositionObjectType>(
  ({ sequence }) => ({
    __typename: "PositionObjectType",
    id: toGlobalId("PositionObjectType", sequence),
    investmentEntity: investmentEntityFactory.build(),
  }),
);

export const positionEdgeFactory = Factory.define<PositionEdge>(() => ({
  __typename: "PositionEdge",
  node: positionObjectTypeFactory.build(),
}));

export const positionConnectionFactory = Factory.define<PositionConnection>(({ params }) => ({
  __typename: "PositionConnection",
  count: 2,
  edges: positionEdgeFactory.buildList(
    params?.count !== undefined && params?.count !== null ? params.count : 2,
  ),
}));

export const accountObjectTypeFactory = Factory.define<AccountObjectType>(({ sequence }) => ({
  __typename: "AccountObjectType",
  id: toGlobalId("AccountObjectType", sequence),
  name: faker.company.name(),
  canViewAccountDetails: false,
  canEditAccountDetails: false,
  canLpcmManageContacts: false,
  canAccessLpcm: false,
  isInvestmentEntity: false,
  gpDisplayName: faker.company.buzzNoun(),
  taxIdRedacted: null,
  arenaId: 1,
  arena: null,
  contacts: null,
  mailingAddress: null,
  paymentPrefs: [],
  paymentMethodSupportedCounts: {
    ach: 0,
    wire: 0,
    check: 0,
  },
  positions: positionConnectionFactory.build(),
}));

export const accountEdgeFactory = Factory.define<AccountEdge>(() => ({
  __typename: "AccountEdge",
  node: accountObjectTypeFactory.build(),
}));

class RedactedPaymentPrefFactory extends Factory<RedactedPaymentPref> {
  ach() {
    return this.params({
      paymentMethod: PaymentMethod.ACH,
      bankInfoRedacted: "Bank of America ACH --redacted",
    });
  }

  wire() {
    return this.params({
      paymentMethod: PaymentMethod.Wire,
      bankInfoRedacted: "Bank of America Wire --redacted",
    });
  }

  bacs() {
    return this.params({
      paymentMethod: PaymentMethod.BACS,
      bankInfoRedacted: "Barclays account ending in 5678",
    });
  }

  becs() {
    return this.params({
      paymentMethod: PaymentMethod.BECS,
      bankInfoRedacted: "Commonwealth Bank account ending in 5678",
    });
  }

  check() {
    return this.params({
      paymentMethod: PaymentMethod.Check,
      bankInfoRedacted: "Bank of America Check --redacted",
    });
  }

  eft() {
    return this.params({
      paymentMethod: PaymentMethod.EFT,
      bankInfoRedacted: "Royal Bank of Canada EFT --redacted",
    });
  }

  other() {
    return this.params({
      paymentMethod: PaymentMethod.Other,
      bankInfoRedacted: "Local bank transfer --redacted",
    });
  }

  sepa() {
    return this.params({
      paymentMethod: PaymentMethod.SEPA,
      bankInfoRedacted: "Deutsche Bank SEPA --redacted",
    });
  }

  verificationError() {
    return this.params({
      verificationStatus: "error",
    });
  }

  unverified() {
    return this.params({
      verificationStatus: "unverified",
    });
  }
}

export const redactedPaymentPrefFactory = RedactedPaymentPrefFactory.define(({ sequence }) => {
  return {
    __typename: "PaymentPrefObjectType" as const,
    id: toGlobalId("PaymentPrefObjectType", sequence),
    paymentMethod: PaymentMethod.ACH,
    bankInfoRedacted: "redacted bank info details",
    verificationStatus: null,
    returnCode: null,
    codeReason: null,
    nocCorrections: null,
  };
});

export interface CheckPaymentPrefInputTypeFactoryTransientParams {
  state?: string | null;
  province?: string | null;
  country?: string | null;
}

export const checkPaymentPrefInputTypeFactory = Factory.define<
  CheckPaymentPrefInputType,
  CheckPaymentPrefInputTypeFactoryTransientParams
>(({ transientParams }) => {
  return {
    memo: "",
    payeeName: "Tark Smithbrow",
    address: {
      city: "San Francisco",
      country: transientParams?.country ?? "United States:Q291bnRyeU9iamVjdFR5cGU6MjM0",
      province: transientParams?.province ?? undefined,
      sameAsMailingAddress: false,
      state: transientParams?.state ?? "CA",
      streetLine1: "123 Street",
      streetLine2: "Apartment 4",
      zip: "94104",
    },
  };
});

export const detailedAchPaymentPrefsFactory = Factory.define<DetailedAchPaymentPrefObjectType>(
  ({ sequence, params }) => {
    const paymentPrefId = params?.paymentPrefId ?? sequence;
    return {
      __typename: "AchPaymentPrefObjectType",
      id: toGlobalId("PaymentPrefObjectType", paymentPrefId),
      paymentPrefId,
      bankName: "Bank of America",
      routingNumber: "111000038",
      accountNumber: "011111111",
      accountName: "John Doe",
      accountType: "checking",
      accountOwnership: "business",
      additionalInstructions: "additional instructions",
      accountHolderType: "legal_entity",
      address: subscriptionAddressFactory.build(),
      bankInfoRedacted: "Bank of America --redacted",
      naturalPersonDateOfBirth: "1990-01-01",
      naturalPersonEmailAddress: "natural@person.com",
      naturalPersonFirstName: "John",
      naturalPersonLastName: "Doe",
      naturalPersonMiddleName: "M.",
      paymentMethod: PaymentMethod.ACH,
      signatureFullName: "signature full name",
    };
  },
);

export const paymentPrefAddressFactory = Factory.define<PaymentPrefAddressObjectType>(() => ({
  __typename: "PaymentPrefAddressObjectType" as const,
  street: "15 Main St",
  street2: "Unit 11",
  street3: "Apt 7",
  city: "Sydney",
  region: "New South Wales",
  state: null,
  postalCode: "2010",
  country: {
    __typename: "CountryObjectType" as const,
    id: "Q291bnRyeU9iamVjdFR5cGU6MTM=",
    resourceId: "13",
    name: "Australia",
    alpha2code: "AU",
    code: 13,
    population: null,
  },
}));

export const detailedBacsPaymentPrefsFactory =
  Factory.define<DetailedBacsPaymentPrefObjectType>(({ sequence, params, associations }) => {
    const paymentPrefId = params?.paymentPrefId ?? sequence;
    return {
      __typename: "BacsPaymentPrefObjectType",
      id: toGlobalId("PaymentPrefObjectType", paymentPrefId),
      paymentPrefId,
      paymentMethod: PaymentMethod.BACS,
      accountName: "John Doe",
      accountNumber: "12345678",
      bank: associations?.bank ?? {
        __typename: "BacsBankObjectType" as const,
        name: "Barclays",
        sortCode: "123456",
        address: paymentPrefAddressFactory.build(),
      },
      additionalInstructions: null,
      accountHolderAddress:
        associations?.accountHolderAddress ?? paymentPrefAddressFactory.build(),
      bankInfoRedacted: "Barclays account ending in 5678",
      positions: null,
    };
  });

export const detailedBecsPaymentPrefsFactory =
  Factory.define<DetailedBecsPaymentPrefObjectType>(({ sequence, params }) => {
    const paymentPrefId = params?.paymentPrefId ?? sequence;
    return {
      __typename: "BecsPaymentPrefObjectType",
      id: toGlobalId("PaymentPrefObjectType", paymentPrefId),
      paymentPrefId,
      paymentMethod: PaymentMethod.BECS,
      accountName: "John Doe",
      accountNumber: "12345678",
      bank: {
        __typename: "BecsBankObjectType",
        bsb: "123456",
        name: "Commonwealth Bank",
        address: {
          __typename: "PaymentPrefAddressObjectType",
          street: null,
          street2: null,
          street3: null,
          city: null,
          region: null,
          postalCode: null,
          state: null,
          country: {
            __typename: "CountryObjectType",
            id: toGlobalId("CountryObjectType", 13),
            name: "Australia",
            alpha2code: "AU",
            code: 61,
          },
        },
      },
      additionalInstructions: null,
      accountHolderAddress: {
        __typename: "PaymentPrefAddressObjectType",
        street: "15 Main St",
        street2: "Unit 11",
        street3: "Apt 7",
        city: "Sydney",
        region: "New South Wales",
        postalCode: "2010",
        state: null,
        country: {
          __typename: "CountryObjectType",
          id: toGlobalId("CountryObjectType", 13),
          name: "Australia",
          alpha2code: "AU",
          code: 61,
        },
      },
      bankInfoRedacted: "Commonwealth Bank account ending in 5678",
    };
  });

class WireBankFullFragmentFactory extends Factory<WireBankFullFragment> {
  intermediary() {
    return this.params({
      name: "Intermediary Test Bank",
      abaNumber: "987654321",
      swiftCode: "abcdefgh123",
      intlRoutingCode: "123",
    });
  }
}

export const wireBankFullFragmentFactory = WireBankFullFragmentFactory.define(
  ({ associations }) => {
    return {
      name: "Test Bank",
      address: associations.address ?? subscriptionAddressFactory.build(),
      abaNumber: "123456780",
      swiftCode: "TESTSWFT",
      intlRoutingCode: "INTL123",
    };
  },
);
export const detailedWirePaymentPrefsFactory =
  Factory.define<DetailedWirePaymentPrefObjectType>(({ sequence, associations }) => {
    return {
      __typename: "WirePaymentPrefObjectType",
      paymentMethod: PaymentMethod.Wire,
      id: toGlobalId("PaymentPrefObjectType", sequence),
      paymentPrefId: sequence,
      beneficiaryName: "Beneficiary Name",
      beneficiaryAddress: associations.beneficiaryAddress ?? subscriptionAddressFactory.build(),
      beneficiaryAccountNumber: "123456789",
      beneficiaryType: "unknown",
      referenceToBeneficiary: "reference",
      wireObi: {
        __typename: "WireExtraFieldObjectType",
        field0: "Obi line1",
        field1: "Obi line2",
        field2: "Obi line3",
        field3: "Obi line4",
      },
      wireBbi: {
        __typename: "WireExtraFieldObjectType",
        field0: "",
        field1: "",
        field2: "",
        field3: "",
      },
      bank: associations.bank ?? wireBankFullFragmentFactory.build(),
      intermediaryBank:
        associations.intermediaryBank ?? wireBankFullFragmentFactory.intermediary().build(),
      bankInfoRedacted: "Bank of America --redacted",
      naturalPersonDateOfBirth: "1990-01-01",
      naturalPersonEmailAddress: "natural@person.com",
      naturalPersonFirstName: "John",
      naturalPersonLastName: "Doe",
      naturalPersonMiddleName: "M.",
    };
  });
export const detailedEftPaymentPrefsFactory = Factory.define<DetailedEftPaymentPrefObjectType>(
  ({ sequence, params }) => {
    const paymentPrefId = params?.paymentPrefId ?? sequence;
    return {
      __typename: "EftPaymentPrefObjectType",
      id: toGlobalId("PaymentPrefObjectType", paymentPrefId),
      paymentPrefId,
      paymentMethod: PaymentMethod.EFT,
      accountName: "Jane Doe",
      bank: {
        __typename: "EftBankObjectType",
        name: "Royal Bank of Canada",
        institutionNumber: "003",
        transitNumber: "12345",
        address: {
          __typename: "PaymentPrefAddressObjectType",
          street: null,
          street2: null,
          street3: null,
          city: null,
          region: null,
          postalCode: null,
          state: null,
          country: {
            __typename: "CountryObjectType",
            id: "Q291bnRyeUlkMQ==",
            name: "Canada",
            alpha2code: null,
            code: null,
          },
        },
      },
      accountNumber: "1234567890",
      additionalInstructions: "Business days only",
      bankInfoRedacted: "Royal Bank of Canada account ending in -67890",
      accountHolderAddress: {
        __typename: "PaymentPrefAddressObjectType",
        street: "100 Wellington St W",
        street2: null,
        street3: null,
        city: "Toronto",
        region: "ON",
        postalCode: "M5K 1A2",
        state: null,
        country: {
          __typename: "CountryObjectType",
          id: "Q291bnRyeUlkMQ==",
          name: "Canada",
          alpha2code: null,
          code: null,
        },
      },
    };
  },
);

class DetailedCheckPaymentPrefsFactory extends Factory<DetailedCheckPaymentPrefObjectType> {
  usa() {
    return this.params({
      address: subscriptionAddressFactory.california().build(),
    });
  }

  australia() {
    return this.params({
      address: subscriptionAddressFactory.australia().build(),
    });
  }
}

export const detailedCheckPaymentPrefsFactory = DetailedCheckPaymentPrefsFactory.define(
  ({ sequence, associations, params }) => {
    const address = associations?.address ?? subscriptionAddressFactory.build();
    const payeeName = params.payeeName || "Random name";
    return {
      __typename: "CheckPaymentPrefObjectType" as const,
      paymentMethod: PaymentMethod.Check,
      id: toGlobalId("PaymentPrefObjectType", sequence),
      paymentPrefId: sequence,
      payeeName,
      address,
      useAccountMailingAddress: false,
      accountHolderType: null,
      naturalPersonFirstName: null,
      naturalPersonMiddleName: null,
      naturalPersonLastName: null,
      naturalPersonDateOfBirth: null,
      naturalPersonEmailAddress: null,
      bankName: null,
      bankCountry: null,
      memo: "",
      bankInfoRedacted: getBankInfoRedacted(payeeName, address),
    };
  },
);

class DetailedPaymentPrefFactory extends Factory<DetailedPaymentPref> {
  ach() {
    return this.params({
      paymentMethod: PaymentMethod.ACH,
    }).associations({
      paymentPrefFull: detailedAchPaymentPrefsFactory.build(),
    });
  }

  wire() {
    return this.params({
      paymentMethod: PaymentMethod.Wire,
    }).associations({
      paymentPrefFull: detailedWirePaymentPrefsFactory.build(),
    });
  }

  becs() {
    return this.params({
      paymentMethod: PaymentMethod.BECS,
    }).associations({
      paymentPrefFull: detailedBecsPaymentPrefsFactory.build(),
    });
  }

  check() {
    return this.params({
      paymentMethod: PaymentMethod.Check,
    }).associations({
      paymentPrefFull: detailedCheckPaymentPrefsFactory.build(),
    });
  }

  eft() {
    return this.params({
      paymentMethod: PaymentMethod.EFT,
    }).associations({
      paymentPrefFull: detailedEftPaymentPrefsFactory.build(),
    });
  }

  bacs() {
    return this.params({
      paymentMethod: PaymentMethod.BACS,
    }).associations({
      paymentPrefFull: detailedBacsPaymentPrefsFactory.build(),
    });
  }

  sepa() {
    return this.params({
      paymentMethod: PaymentMethod.SEPA,
    }).associations({
      paymentPrefFull: detailedSepaPaymentPrefsFactory.build(),
    });
  }

  other() {
    return this.params({
      paymentMethod: PaymentMethod.Other,
    }).associations({
      paymentPrefFull: detailedOtherPaymentPrefsFactory.build(),
    });
  }
}

export const detailedPaymentPrefFactory = DetailedPaymentPrefFactory.define(
  ({ sequence, associations }) => {
    return {
      __typename: "PaymentPrefObjectType" as const,
      id: toGlobalId("PaymentPrefObjectType", sequence),
      bankInfoRedacted: null,
      checkAddrCity: null,
      checkAddrCountry: null,
      checkAddrCountryId: null,
      checkAddrPostalCode: null,
      checkAddrRegion: null,
      checkAddrState: null,
      checkAddrStateId: null,
      checkAddrStreet: null,
      checkAddrStreet2: null,
      checkAddrStreet3: null,
      checkMailingAddressSameAsAccount: null,
      checkMemoField: null,
      checkPayeeOverride: null,
      paymentMethod: null,
      paymentPrefFull: associations?.paymentPrefFull ?? null,
      wireHasIntermediary: null,
      wireHasInternationalBeneficiaryAddress: null,
      verificationStatus: null,
      returnCode: null,
      codeReason: null,
      nocCorrections: null,
      watchlistStatus: associations?.watchlistStatus ?? [],
    };
  },
);

export const detailedSepaPaymentPrefsFactory =
  Factory.define<DetailedSepaPaymentPrefObjectType>(({ sequence, params }) => {
    const paymentPrefId = params?.paymentPrefId ?? sequence;
    return {
      __typename: "SepaPaymentPrefObjectType",
      id: toGlobalId("PaymentPrefObjectType", paymentPrefId),
      paymentPrefId,
      paymentMethod: PaymentMethod.SEPA,
      bank: {
        __typename: "SepaBankObjectType",
        name: "Deutsche Bank",
        bicSwiftCode: "DEUTDEFF",
        address: {
          __typename: "PaymentPrefAddressObjectType",
          street: null,
          street2: null,
          street3: null,
          city: null,
          region: null,
          state: null,
          postalCode: null,
          country: {
            __typename: "CountryObjectType",
            id: "Q291bnRyeUlkMg==",
            name: "Germany",
            alpha2code: null,
            code: null,
          },
        },
      },
      iban: "DE89370400440532013000",
      accountName: "John Doe",
      additionalInstructions: "Business days only",
      accountHolderAddress: {
        __typename: "PaymentPrefAddressObjectType" as const,
        street: "Hauptstraße 123",
        street2: null,
        street3: null,
        city: "Frankfurt",
        region: "Hesse",
        state: null,
        postalCode: "60311",
        country: {
          __typename: "CountryObjectType" as const,
          id: "Q291bnRyeU9iamVjdFR5cGU6NTk=",
          name: "Germany",
          alpha2code: null,
          code: null,
        },
      },
      bankInfoRedacted: "Deutsche Bank IBAN ending in -3000",
    };
  });

export const detailedOtherPaymentPrefsFactory =
  Factory.define<DetailedNonUsPaymentPrefObjectType>(({ sequence, params }) => {
    const paymentPrefId = params?.paymentPrefId ?? sequence;
    return {
      __typename: "NonUsPaymentPrefObjectType",
      id: toGlobalId("PaymentPrefObjectType", paymentPrefId),
      paymentPrefId,
      paymentMethod: "Other",
      otherPaymentPrefLabel: "Local bank transfer",
      nonUsPaymentPrefInstructions: "Send funds via local bank rails.",
      bankInfoRedacted: null,
      positions: null,
    };
  });
